import { useGlobal } from 'hooks/useGlobal';
import { useRecoilValue } from 'recoil';
import numeral from 'numeral';
import produce from 'immer';
import { isNumber } from 'lodash';

import * as K from 'common/constants';
import { useSetSearchParams } from 'hooks/useSetSearchParams';
import { getDisplayValue } from 'common/util/graph';
import { PlotModel, UrlParams } from 'common/types';
import { queryTimeRangeState, searchParamRecordState } from 'components/common/Managers/RouteStateManager.state';
import { useResizeRegistration } from 'components/common/Managers/LayoutStateManager';
import PlotGroupPicker from 'components/common/Legends/SearchGraphLegend/PlotGroupPicker';

import './PowerQueryChartLegend.scss';

type PowerQueryChartLegendProps = {
  plotGroups?: Record<string, PlotModel[]>;
  timeStamps?: Array<number>;
};

/**
 * Legend component for the Chart views on PQ page. This allows user to hide/show plots and updates the URL so
 * they can save that plot visibility state to a saved search. (Does not apply when saving to a dashboard)
 */
function PowerQueryChartLegend({ plotGroups, timeStamps }: PowerQueryChartLegendProps): JSX.Element {
  const searchParams = useRecoilValue(searchParamRecordState);

  const setSearchParams = useSetSearchParams();
  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [selectionRange] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const sideBarContainerRef = useResizeRegistration();

  const displayPlotGroups = produce(plotGroups, (draft) => {
    Object.values(draft || {}).forEach((plots) => {
      plots.forEach((plot) => {
        let start = parseFloat(startTime);
        let end = parseFloat(endTime);
        // TODO get selection average calculations working for PQ graphs.
        if (selectionRange.start && selectionRange.end) {
          start = selectionRange.start;
          end = selectionRange.end;
        }

        let displayValue;
        if (plot.pieData) {
          displayValue = plot.pieData;
        } else {
          displayValue = getDisplayValue(plot, null, start, end, timeStamps);
        }
        const formatValue = isNumber(displayValue)
          ? numeral(displayValue).format('0,0[.][00000] a').toUpperCase()
          : null;
        plot.value = formatValue;
      });
    });
  });

  const handleTogglePlot = (plotToToggle: PlotModel): void => {
    const showGraphsList = searchParams.showGraphs ? JSON.parse(searchParams.showGraphs) : [];
    const hideGraphsList = searchParams.hideGraphs ? JSON.parse(searchParams.hideGraphs) : [];
    const showGraphs = new Set(showGraphsList);
    const hideGraphs = new Set(hideGraphsList);

    if (plotToToggle.visible) {
      showGraphs.delete(plotToToggle.op);
      hideGraphs.add(plotToToggle.op);
    } else {
      showGraphs.add(plotToToggle.op);
      hideGraphs.delete(plotToToggle.op);
    }
    setSearchParams(
      {
        [K.URL_PARAM.SHOW_GRAPHS]: JSON.stringify(Array.from(showGraphs)),
        [K.URL_PARAM.HIDE_GRAPHS]: JSON.stringify(Array.from(hideGraphs)),
      } as UrlParams,
      false,
    );
  };

  const handleTogglePlotGroup = (title: string, toggleState: boolean): void => {
    const showGraphsList = searchParams.showGraphs ? JSON.parse(searchParams.showGraphs) : [];
    const hideGraphsList = searchParams.hideGraphs ? JSON.parse(searchParams.hideGraphs) : [];
    const showGraphs = new Set(showGraphsList);
    const hideGraphs = new Set(hideGraphsList);

    const plotsToToggle = plotGroups?.[title].map((plot) => plot.op);
    plotsToToggle?.forEach((plot) => {
      if (toggleState) {
        showGraphs.add(plot);
        hideGraphs.delete(plot);
      } else {
        showGraphs.delete(plot);
        hideGraphs.add(plot);
      }
    });
    setSearchParams(
      {
        [K.URL_PARAM.SHOW_GRAPHS]: JSON.stringify(Array.from(showGraphs)),
        [K.URL_PARAM.HIDE_GRAPHS]: JSON.stringify(Array.from(hideGraphs)),
      } as UrlParams,
      false,
    );
  };

  return (
    <div className="PowerQueryChartLegend" ref={sideBarContainerRef} data-resize-id={K.RESIZE_ID.SEARCH_GRAPHS_LEGEND}>
      {Object.entries(displayPlotGroups || {}).map(([key, value]) => (
        <PlotGroupPicker
          key={key}
          title={key}
          plots={value}
          togglePlot={handleTogglePlot}
          togglePlotGroup={handleTogglePlotGroup}
        />
      ))}
    </div>
  );
}

export default PowerQueryChartLegend;
